import { first, last } from 'lodash'

import { ScriptWhereItem } from 'stores/script/ScriptWhere'
import Field from 'stores/field/Field'

const isWhereFilterValid = (where: ScriptWhereItem): boolean => {
  if (!where.enabled) {
    // Disabled filters are always valid
    return true
  }

  if (!last(where.fields)) {
    return false
  }

  if (!where.operation) {
    return false
  }

  if (!last(where.fields)?.isScalar && !last(where.fields)?.isEnum) {
    // The last field must be always be a scalar or an enum
    return false
  }

  if (where.fields.length > 1 && !first(where.fields)?.isRelation) {
    // If there are multiple scalar fields in the fields list, it is invalid
    return false
  }

  if (
    ['isNull', 'isNotNull'].includes(where.operation) &&
    !last(where.fields)?.isRequired
  ) {
    // These are synthetic operations (not supported by Prisma Client) that are valid when a field is optional
    return true
  }

  if (
    last(where.fields)?.isRequired &&
    (where.value === undefined || where.value === null)
  ) {
    return false
  }

  if (where.value === undefined || where.value === null) {
    return false
  }

  if (['in', 'notIn'].includes(where.operation)) {
    try {
      const parsedValue = JSON.parse(where.value)
      return (
        Array.isArray(parsedValue) &&
        parsedValue.every(v => {
          if (last(where.fields)?.isScalar) {
            return isScalarValid(last(where.fields) as Field, v)
          }
          if (last(where.fields)?.isEnum)
            return isEnumValid(last(where.fields) as Field, v)
          return false
        })
      )
    } catch (e) {
      return false
    }
  }

  if (!where.supportedOperations.includes(where.operation)) {
    return false
  }

  if (last(where.fields)?.isScalar) {
    return isScalarValid(last(where.fields) as Field, where.value)
  }

  if (last(where.fields)?.isEnum) {
    return isEnumValid(last(where.fields) as Field, where.value)
  }

  // At this point, we know the field is a relation (or relation list)

  return true
}

const isScalarValid = (field: Field, scalar: string): boolean => {
  // We do not use isFieldValueInvalid util here because we do not want the extra isRequired checks etc that it brings, they are not relevant to us here
  if (!field.isScalar) {
    return false
  }

  if (field.isString) {
    return typeof scalar === 'string'
  }

  if (field.isInt || field.isFloat || field.isDecimal) {
    return scalar !== '' && !isNaN(Number(scalar))
  }

  if (field.isBigInt) {
    try {
      return scalar !== '' && !!BigInt(scalar)
    } catch (e) {
      return false
    }
  }

  if (field.isBoolean) {
    return scalar === 'true' || scalar === 'false'
  }

  if (field.isDateTime) {
    return !isNaN(new Date(scalar) as any)
  }

  if (field.isJson) {
    try {
      return !!JSON.parse(scalar)
    } catch (e) {
      return false
    }
  }

  if (field.isBytes) {
    return typeof scalar === 'string' // Don't try to verify base64 strings
  }

  return false
}

const isEnumValid = (field: Field, enumerable: string) => {
  if (!field.isEnum) {
    return false
  }

  return field.typeAsEnum!.values.includes(enumerable)
}

export default isWhereFilterValid
